/**
 * PSP/CRE Tool v2.0 - Surgical Capacity Constants
 * 
 * Defines surgical capacity parameters for military medical planning:
 * - Surgical team composition and capability
 * - Operating room throughput
 * - Damage Control Surgery (DCS) phases
 * - Surgical fatigue and degradation
 * - Procedure duration standards
 * - Anesthesia capacity
 * - Sterilization/CSSD capacity
 * - Mass casualty surgical protocols
 * 
 * Key Concepts:
 * - Damage Control Surgery (DCS): 3-phase approach
 *   Phase 1: OR - Hemorrhage/contamination control (45-90 min)
 *   Phase 2: ICU - Resuscitation (24-48 hours)
 *   Phase 3: OR - Definitive repair (2-6+ hours)
 * - Surgical fatigue: Performance degrades over time
 * - Team rotation: Required for sustained operations
 * 
 * Based on:
 * - NATO AMedP-1.7 Medical Planning Factors
 * - US Army ATP 4-02.10 Theater Hospitalization
 * - TCCC Guidelines and DCS principles
 * - Ukraine 2022-2024 surgical experience
 * - Israeli Defense Forces trauma protocols
 * 
 * Author: Dr. Tatar Radu MD
 * Date: December 20, 2024
 * Version: 2.2 - FRT-CONST: Add FORWARD_RESUSCITATION_TEAM to SURGICAL_TEAM_COMPOSITION (March 28, 2026)
 *   ROOT CAUSE: Tab3_Medical.jsx line ~6095 referenced
 *     SURGICAL_TEAM_COMPOSITION?.FORWARD_RESUSCITATION_TEAM?.personnel?.total
 *   but the key did not exist — fell back to || 8 (correct value, APP_RULES §6.1 silent fallback).
 *   FIX: Add FORWARD_RESUSCITATION_TEAM entry. Personnel total = 8 (matches fallback — no behaviour change).
 *   Inserted before FORWARD_SURGICAL_TEAM (Role 1+ precedes Role 2 in echelon order).
 *   DOW modifier reference added: FRT_DOW_REDUCTION_PERCENT = 7 in theatreHoldingPolicy.js.
 *
 * Version: 2.1 - EXT-04: Add SURGERY_RATE_BY_TRIAGE (February 22, 2026)
 *
 * Changes v2.1 (February 22, 2026) - EXT-04:
 * - ADD: `SURGERY_RATE_BY_TRIAGE` — OR time allocation weight per triage category.
 *   DISTINCTION from SURGERY_REQUIREMENTS_BY_TRIAGE.surgery_required_percent:
 *     surgery_required_percent = what proportion of patients in this category need surgery
 *     SURGERY_RATE_BY_TRIAGE   = relative OR time weight for scheduling (T1=full slot, T2=half)
 *   Consumed by: timeToSurgeryModule.js OR scheduling, supplyIntelligence.js throughput.
 * - NOTE: SURGICAL_TEAM_COMPOSITION.FORWARD_SURGICAL_TEAM already contains comprehensive FST
 *   specs. No new FST data needed — timeToSurgeryModule.js local SURGICAL_TEAMS will
 *   reference this constant in Phase 3/4.
 * - Removed: @institution Romanian Military Medical Institute (APP_RULES 5.3).
 * 
 * Changes from v1.0:
 * - Added DCS 3-phase model with detailed parameters
 * - Added specialty-specific surgical teams
 * - Added Ukraine 2022-2024 lessons learned
 * - Added anesthesia capacity planning
 * - Added sterilization/CSSD capacity
 * - Added mass casualty surgical protocols
 * - Added surgical priority scoring
 * - Added mechanism-specific procedure mapping
 * - Enhanced calculator integration functions
 */

// ============================================================================
// DAMAGE CONTROL SURGERY (DCS) PHASES
// ============================================================================

/**
 * DCS is a 3-phase approach to manage severely injured patients
 * Critical for modern military surgery
 */
export const DCS_PHASES = {
  PHASE_1: {
    name: 'Initial Surgery - Damage Control',
    location: 'Operating Room',
    duration_minutes: { min: 30, typical: 60, max: 90 },
    
    objectives: [
      'Control hemorrhage',
      'Control contamination',
      'Temporary vascular shunts',
      'Temporary abdominal closure',
      'Pack and close'
    ],
    
    NOT_objectives: [
      'Definitive repair',
      'Anastomosis',
      'Definitive closure',
      'Complex reconstruction'
    ],
    
    procedures: {
      laparotomy: { duration_min: 45, typical: 60 },
      thoracotomy: { duration_min: 30, typical: 45 },
      vascular_shunt: { duration_min: 20, typical: 30 },
      packing: { duration_min: 15, typical: 20 },
      fasciotomy: { duration_min: 15, typical: 25 },
      amputation: { duration_min: 20, typical: 35 }
    },
    
    blood_products: {
      prbc_units_avg: 8,
      ffp_units_avg: 6,
      mtp_activation_rate: 0.60
    },
    
    success_criteria: {
      hemorrhage_controlled: true,
      contamination_controlled: true,
      temp_above_35c: true,
      ph_above_7_2: true,
      coagulopathy_improving: true
    }
  },
  
  PHASE_2: {
    name: 'ICU Resuscitation',
    location: 'Intensive Care Unit',
    duration_hours: { min: 12, typical: 36, max: 72 },
    
    objectives: [
      'Correct hypothermia (>36°C)',
      'Correct acidosis (pH >7.35)',
      'Correct coagulopathy (INR <1.5)',
      'Optimize perfusion',
      'Restore intravascular volume',
      'Prevent secondary injury'
    ],
    
    targets: {
      temperature_c: 36.5,
      ph: 7.40,
      lactate_mmol: 2.0,
      base_deficit: 0,
      inr: 1.3,
      platelet_count: 100000,
      fibrinogen_gdl: 2.0,
      calcium_mmol: 1.1
    },
    
    monitoring: {
      vitals_frequency_hours: 1,
      labs_frequency_hours: 4,
      abdominal_exam_hours: 4,
      compartment_check_hours: 2
    },
    
    criteria_for_phase_3: [
      'Temperature >36°C for >6 hours',
      'No vasopressors or minimal',
      'INR <1.5',
      'Lactate normalizing',
      'No ongoing hemorrhage',
      'Hemodynamically stable'
    ]
  },
  
  PHASE_3: {
    name: 'Definitive Surgery',
    location: 'Operating Room',
    duration_hours: { min: 2, typical: 4, max: 8 },
    timing: '24-72 hours after Phase 1',
    
    objectives: [
      'Remove packing',
      'Definitive repair/reconstruction',
      'Anastomosis',
      'Definitive closure',
      'Fracture fixation (if stable)',
      'Soft tissue coverage'
    ],
    
    procedures: {
      laparotomy_definitive: { duration_hours: 3 },
      vascular_repair: { duration_hours: 3 },
      bowel_anastomosis: { duration_hours: 2 },
      abdominal_closure: { duration_hours: 1 },
      orif: { duration_hours: 3 },
      skin_grafting: { duration_hours: 2 }
    },
    
    may_require_multiple_returns: true,
    average_returns_to_or: 2.3,
    
    notes: 'Only proceed when Phase 2 criteria met. May defer orthopedic fixation.'
  },
  
  // DCS success rates
  outcomes: {
    overall_survival: 0.75,  // 75% survival with DCS approach
    survival_without_dcs: 0.40,  // 40% without DCS
    survival_improvement: 0.35,  // 35% absolute improvement
    
    // Ukraine 2022-2024 data
    ukraine_data: {
      dcs_rate: 0.65,  // 65% of surgical casualties received DCS
      survival_rate: 0.78,
      average_or_returns: 2.8,
      average_icu_days: 4.2
    }
  }
};

// ============================================================================
// SURGICAL TEAM COMPOSITION
// ============================================================================

export const SURGICAL_TEAM_COMPOSITION = {

  // ── Role 1+ ─────────────────────────────────────────────────────────────────
  // FRT-CONST: Added v2.2 (March 28, 2026). Resolves Tab3 silent fallback || 8.
  // NO surgery. Damage Control Resuscitation (DCR) only.
  // DOW modifier: FRT_DOW_REDUCTION_PERCENT = 7 in theatreHoldingPolicy.js.
  FORWARD_RESUSCITATION_TEAM: {
    code: 'FRT',
    name: 'Forward Resuscitation Team',
    nato_designation: 'FRT',
    typical_location: 'Role 1+',

    personnel: {
      emergency_physicians: 2,
      paramedics: 4,
      combat_medics: 2,
      total: 8   // matches Tab3 fallback — no behaviour change
    },

    capability: {
      simultaneous_tables: 0,
      procedures_per_day: 0,
      max_procedures_per_day: 0,
      specialties: [
        'Damage Control Resuscitation',
        'Hemorrhage Control',
        'Airway Management',
        'Whole Blood Transfusion',
        'POCUS Assessment'
      ],
      surgery: false,
      dcs_capable: false,
      dcr_capable: true,
      definitive_capable: false,
      neurosurgery: false,
      vascular: 'NONE'
    },

    equipment: {
      operating_tables: 0,
      ventilators: 1,
      monitors: 2,
      suction_units: 2,
      electrosurgery: 0,
      sterilizer: 0,
      xray: false,
      ultrasound: true,       // POCUS for rapid haemorrhage assessment
      whole_blood_kit: true   // pre-typed whole blood + administration set
    },

    consumables_days: 2,
    blood_storage_units: 6,   // pre-typed whole blood units

    deployability: {
      setup_time_hours: 1,
      teardown_time_hours: 0.5,
      transport_vehicles: 1,
      weight_kg: 500,
      can_be_helicopter_inserted: true
    },

    // Reference to the DOW modifier this team applies in casualtyCalculator.js.
    // Binary: >=1 FRT deployed → frtDOWModifier = 0.93 (−7% DOW rate). No stacking.
    dow_modifier: {
      reduction_percent: 7,
      constant_ref: 'FRT_DOW_REDUCTION_PERCENT',
      source: 'theatreHoldingPolicy.js',
      application: 'Binary — >=1 FRT deployed applies full 7% reduction. 0 FRTs = no effect.',
      mechanism: 'Earlier DCR at point of injury reduces preventable death before evacuation'
    },

    notes: 'Role 1+ Forward. NO surgery. DCR only: hemorrhage control, airway, ' +
           'whole blood, POCUS. Deploys with maneuver elements at brigade level. ' +
           'Source: AJMedP-1 Section 6 Role 1+ concept; Ukraine 2022-2024 FRT data.'
  },

  // ── Role 2 ──────────────────────────────────────────────────────────────────
  FORWARD_SURGICAL_TEAM: {
    code: 'FST',
    name: 'Forward Surgical Team',
    nato_designation: 'FST',
    typical_location: 'Role 2',
    
    personnel: {
      surgeons: 3,
      anesthesiologists: 1,
      crna: 1,
      or_nurses: 4,
      surgical_techs: 2,
      medics: 4,
      admin: 1,
      total: 16
    },
    
    capability: {
      simultaneous_tables: 2,
      procedures_per_day: 10,
      max_procedures_per_day: 16,
      specialties: ['General Surgery', 'Orthopedic', 'Trauma'],
      dcs_capable: true,
      definitive_capable: false,
      neurosurgery: false,
      vascular: 'BASIC'
    },
    
    equipment: {
      operating_tables: 2,
      ventilators: 4,
      monitors: 6,
      suction_units: 4,
      electrosurgery: 2,
      sterilizer: 1,
      xray: false,
      ultrasound: true
    },
    
    consumables_days: 3,
    blood_storage_units: 20,
    
    deployability: {
      setup_time_hours: 4,
      teardown_time_hours: 2,
      transport_vehicles: 2,
      weight_kg: 3000,
      can_be_helicopter_inserted: true
    },
    
    notes: 'Rapid deployment. Focus on damage control. Must evacuate post-op.'
  },
  
  SURGICAL_COMPANY: {
    code: 'SURG_CO',
    name: 'Surgical Company',
    nato_designation: 'Role 2E',
    typical_location: 'Role 2 Enhanced',
    
    personnel: {
      surgeons: 8,
      anesthesiologists: 4,
      crna: 2,
      or_nurses: 12,
      surgical_techs: 6,
      icu_nurses: 8,
      medics: 10,
      lab_tech: 2,
      xray_tech: 2,
      cssd_tech: 2,
      admin: 4,
      total: 60
    },
    
    capability: {
      simultaneous_tables: 4,
      procedures_per_day: 24,
      max_procedures_per_day: 40,
      specialties: ['General Surgery', 'Orthopedic', 'Trauma', 'Vascular', 'Thoracic', 'Neurosurgery (basic)'],
      dcs_capable: true,
      definitive_capable: true,
      neurosurgery: 'BASIC',
      vascular: 'FULL'
    },
    
    equipment: {
      operating_tables: 4,
      ventilators: 12,
      monitors: 16,
      suction_units: 8,
      electrosurgery: 4,
      sterilizers: 2,
      xray_portable: 1,
      ultrasound: 2,
      cell_saver: 1
    },
    
    consumables_days: 7,
    blood_storage_units: 100,
    
    deployability: {
      setup_time_hours: 12,
      teardown_time_hours: 6,
      transport_vehicles: 8,
      weight_kg: 15000
    },
    
    notes: 'Full surgical capability. Can perform definitive surgery. Post-op holding.'
  },
  
  CSH_SURGICAL: {
    code: 'CSH_SURG',
    name: 'CSH Surgical Department',
    nato_designation: 'Role 3',
    typical_location: 'Role 3',
    
    personnel: {
      surgeons: 16,
      anesthesiologists: 8,
      crna: 4,
      or_nurses: 24,
      surgical_techs: 12,
      icu_nurses: 20,
      pacu_nurses: 8,
      medics: 20,
      lab_techs: 6,
      xray_techs: 4,
      blood_bank: 4,
      cssd_techs: 6,
      admin: 8,
      total: 140
    },
    
    capability: {
      simultaneous_tables: 8,
      procedures_per_day: 48,
      max_procedures_per_day: 80,
      specialties: [
        'General Surgery', 'Orthopedic', 'Trauma', 'Vascular', 'Thoracic',
        'Neurosurgery', 'Maxillofacial', 'ENT', 'Ophthalmology', 'Urology',
        'Plastic/Reconstructive'
      ],
      dcs_capable: true,
      definitive_capable: true,
      neurosurgery: 'FULL',
      vascular: 'FULL',
      burns: true,
      reimplantation: 'LIMITED'
    },
    
    equipment: {
      operating_tables: 8,
      ventilators: 40,
      monitors: 60,
      suction_units: 16,
      electrosurgery: 8,
      sterilizers: 4,
      ct_scanner: 1,
      xray: 2,
      ultrasound: 4,
      fluoroscopy: 1,
      cell_saver: 2,
      microscope: 1
    },
    
    consumables_days: 14,
    blood_storage_units: 300,
    
    deployability: {
      setup_time_hours: 72,
      teardown_time_hours: 48,
      transport_vehicles: 40,
      weight_kg: 100000
    },
    
    notes: 'Theater surgical capability. Full spectrum of care. Extended recovery.'
  }
};

// ============================================================================
// SPECIALTY-SPECIFIC SURGICAL TEAMS
// ============================================================================

export const SPECIALTY_SURGICAL_TEAMS = {
  NEUROSURGERY: {
    specialty: 'NEUROSURGERY',
    personnel: {
      neurosurgeon: 1,
      anesthesiologist: 1,
      or_nurse: 2,
      surgical_tech: 1
    },
    procedures_per_day: 4,
    max_procedures_per_day: 6,
    
    procedures: {
      craniotomy_decompressive: { duration_hours: 1.5, blood_units: 4 },
      craniotomy_hematoma: { duration_hours: 2, blood_units: 4 },
      burr_holes: { duration_hours: 0.5, blood_units: 1 },
      spine_decompression: { duration_hours: 2, blood_units: 3 },
      icp_monitor: { duration_hours: 0.5, blood_units: 0 }
    },
    
    equipment_required: ['Craniotome', 'Microscope', 'ICP monitor'],
    
    ukraine_demand: {
      percentage_of_casualties: 0.12,
      head_injury_rate: 0.28,
      specialist_shortage: true,
      notes: 'Significant shortage of neurosurgeons forward'
    }
  },
  
  VASCULAR: {
    specialty: 'VASCULAR',
    personnel: {
      vascular_surgeon: 1,
      anesthesiologist: 1,
      or_nurse: 2,
      surgical_tech: 1
    },
    procedures_per_day: 6,
    max_procedures_per_day: 10,
    
    procedures: {
      shunt_placement: { duration_hours: 0.75, blood_units: 4 },
      arterial_repair: { duration_hours: 2, blood_units: 6 },
      venous_repair: { duration_hours: 1.5, blood_units: 4 },
      bypass_graft: { duration_hours: 3, blood_units: 6 },
      fasciotomy: { duration_hours: 0.5, blood_units: 1 },
      amputation: { duration_hours: 1, blood_units: 4 }
    },
    
    equipment_required: ['Vascular instruments', 'Shunts', 'Grafts', 'Doppler'],
    
    ukraine_demand: {
      percentage_of_casualties: 0.15,
      extremity_vascular: 0.65,
      shunt_rate: 0.40,
      notes: 'High vascular injury rate from fragments'
    }
  },
  
  ORTHOPEDIC: {
    specialty: 'ORTHOPEDIC',
    personnel: {
      orthopedic_surgeon: 1,
      anesthesiologist: 1,
      or_nurse: 2,
      surgical_tech: 1
    },
    procedures_per_day: 8,
    max_procedures_per_day: 12,
    
    procedures: {
      external_fixation: { duration_hours: 1, blood_units: 2 },
      orif_major: { duration_hours: 3, blood_units: 4 },
      orif_minor: { duration_hours: 1.5, blood_units: 1 },
      amputation: { duration_hours: 1, blood_units: 4 },
      debridement: { duration_hours: 0.75, blood_units: 1 },
      fasciotomy: { duration_hours: 0.5, blood_units: 1 }
    },
    
    equipment_required: ['External fixators', 'Power instruments', 'Implants', 'C-arm'],
    
    ukraine_demand: {
      percentage_of_casualties: 0.45,
      extremity_injuries: 0.60,
      amputation_rate: 0.12,
      notes: 'Highest surgical demand by volume'
    }
  },
  
  MAXILLOFACIAL: {
    specialty: 'MAXILLOFACIAL',
    personnel: {
      maxfax_surgeon: 1,
      anesthesiologist: 1,
      or_nurse: 2,
      surgical_tech: 1
    },
    procedures_per_day: 6,
    max_procedures_per_day: 10,
    
    procedures: {
      mandible_fixation: { duration_hours: 2, blood_units: 2 },
      midface_repair: { duration_hours: 3, blood_units: 3 },
      orbital_repair: { duration_hours: 2, blood_units: 2 },
      tracheostomy: { duration_hours: 0.5, blood_units: 0 },
      soft_tissue_repair: { duration_hours: 1.5, blood_units: 1 }
    },
    
    equipment_required: ['Plating systems', 'Arch bars', 'Microscope'],
    
    ukraine_demand: {
      percentage_of_casualties: 0.15,
      drone_related: 0.45,
      notes: 'High demand due to drone head/neck injuries'
    }
  },
  
  BURNS: {
    specialty: 'BURNS',
    personnel: {
      burn_surgeon: 1,
      anesthesiologist: 1,
      or_nurse: 3,
      surgical_tech: 1
    },
    procedures_per_day: 4,
    max_procedures_per_day: 6,
    
    procedures: {
      escharotomy: { duration_hours: 0.5, blood_units: 1 },
      excision_grafting: { duration_hours: 3, blood_units: 8 },
      skin_grafting: { duration_hours: 2, blood_units: 4 },
      debridement: { duration_hours: 1, blood_units: 2 }
    },
    
    equipment_required: ['Dermatome', 'Skin mesher', 'Cadaver skin', 'Xenograft'],
    
    ukraine_demand: {
      percentage_of_casualties: 0.08,
      vehicle_related: 0.80,
      notes: 'High resource consumption per patient'
    }
  }
};

// ============================================================================
// ANESTHESIA CAPACITY
// ============================================================================

export const ANESTHESIA_CAPACITY = {
  PERSONNEL: {
    anesthesiologist: {
      cases_per_day: 6,
      max_cases_per_day: 10,
      supervision_ratio: 2,  // Can supervise 2 CRNAs
      recovery_requirements: 'Same as surgeon'
    },
    crna: {
      cases_per_day: 4,
      max_cases_per_day: 8,
      requires_supervision: true
    }
  },
  
  EQUIPMENT_PER_TABLE: {
    anesthesia_machine: 1,
    ventilator: 1,
    monitors: 1,
    suction: 1,
    infusion_pumps: 4,
    warming_device: 1
  },
  
  CONSUMABLES_PER_CASE: {
    propofol_mg: 200,
    fentanyl_mcg: 250,
    rocuronium_mg: 50,
    ketamine_mg: 100,
    midazolam_mg: 5,
    etomidate_mg: 20,
    succinylcholine_mg: 100,
    
    iv_tubing_sets: 2,
    syringes: 10,
    ett_size_avg: 7.5,
    lma_backup: 1
  },
  
  SPECIAL_CONSIDERATIONS: {
    massive_transfusion: {
      additional_monitoring: true,
      calcium_replacement: true,
      warming_required: true,
      potassium_monitoring: true
    },
    tbi: {
      avoid_hypotension: true,
      avoid_hypoxia: true,
      avoid_hyperventilation: true,
      map_target: 80
    },
    burns: {
      fluid_resuscitation: 'Parkland formula',
      airway_early: true,
      succinylcholine_contraindicated_after_24h: true
    }
  },
  
  FATIGUE_CONSIDERATIONS: {
    same_as_surgeons: true,
    critical_for_safety: true,
    medication_errors_with_fatigue: 0.08,  // 8% error rate when fatigued
    notes: 'Anesthesia errors often immediately life-threatening'
  }
};

// ============================================================================
// STERILIZATION / CSSD CAPACITY
// ============================================================================

export const STERILIZATION_CAPACITY = {
  CSSD_REQUIREMENTS: {
    staff_per_or: 0.5,  // 1 CSSD tech per 2 ORs
    
    equipment: {
      autoclave_small: { capacity_sets: 2, cycle_minutes: 45 },
      autoclave_large: { capacity_sets: 6, cycle_minutes: 60 },
      washer_disinfector: { capacity_sets: 4, cycle_minutes: 30 },
      ultrasonic_cleaner: { capacity_sets: 2, cycle_minutes: 15 }
    },
    
    instrument_sets_needed: {
      laparotomy: 3,
      thoracotomy: 2,
      vascular: 2,
      orthopedic: 4,
      minor: 6,
      debridement: 6
    },
    
    turnaround_time_hours: 2,
    sets_per_day_per_table: 4
  },
  
  FLASH_STERILIZATION: {
    cycle_minutes: 10,
    for_emergency_only: true,
    not_recommended_for_implants: true,
    infection_risk_increase: 0.02  // 2% increased SSI risk
  },
  
  FORWARD_CONSTRAINTS: {
    limited_water: true,
    limited_power: true,
    disposable_preference: true,
    instrument_rationing: true
  }
};

// ============================================================================
// PROCEDURE DURATIONS
// ============================================================================

export const PROCEDURE_DURATIONS = {
  DAMAGE_CONTROL: {
    laparotomy: {
      name: 'Damage Control Laparotomy',
      duration_minutes: { min: 45, typical: 60, max: 90 },
      blood_units: 10,
      priority: 'IMMEDIATE',
      can_defer_closure: true,
      dcs_phase: 1
    },
    thoracotomy: {
      name: 'Emergency Thoracotomy',
      duration_minutes: { min: 30, typical: 45, max: 75 },
      blood_units: 12,
      priority: 'IMMEDIATE',
      can_defer_closure: true,
      dcs_phase: 1
    },
    vascular_shunt: {
      name: 'Vascular Shunt Placement',
      duration_minutes: { min: 30, typical: 45, max: 60 },
      blood_units: 6,
      priority: 'URGENT',
      can_defer_closure: false,
      limb_salvage: true
    },
    amputation: {
      name: 'Traumatic Amputation Completion',
      duration_minutes: { min: 30, typical: 45, max: 60 },
      blood_units: 4,
      priority: 'URGENT',
      can_defer_closure: false
    },
    fasciotomy: {
      name: 'Fasciotomy',
      duration_minutes: { min: 20, typical: 30, max: 45 },
      blood_units: 1,
      priority: 'URGENT',
      can_defer_closure: true,
      compartment_syndrome: true
    },
    craniotomy_decompressive: {
      name: 'Decompressive Craniotomy',
      duration_minutes: { min: 60, typical: 90, max: 120 },
      blood_units: 4,
      priority: 'IMMEDIATE',
      specialty_required: 'NEUROSURGERY'
    },
    packing: {
      name: 'Abdominal/Pelvic Packing',
      duration_minutes: { min: 15, typical: 25, max: 40 },
      blood_units: 8,
      priority: 'IMMEDIATE'
    }
  },
  
  DEFINITIVE: {
    laparotomy_definitive: {
      name: 'Definitive Laparotomy',
      duration_minutes: { min: 120, typical: 180, max: 300 },
      blood_units: 6,
      priority: 'SCHEDULED',
      dcs_phase: 3
    },
    vascular_repair: {
      name: 'Vascular Repair/Graft',
      duration_minutes: { min: 120, typical: 180, max: 240 },
      blood_units: 6,
      priority: 'URGENT',
      specialty_required: 'VASCULAR'
    },
    bowel_anastomosis: {
      name: 'Bowel Anastomosis',
      duration_minutes: { min: 90, typical: 120, max: 180 },
      blood_units: 2,
      priority: 'SCHEDULED',
      dcs_phase: 3
    },
    orif_major: {
      name: 'ORIF Major Fracture',
      duration_minutes: { min: 120, typical: 180, max: 300 },
      blood_units: 4,
      priority: 'DELAYED',
      specialty_required: 'ORTHOPEDIC'
    },
    orif_minor: {
      name: 'ORIF Minor Fracture',
      duration_minutes: { min: 60, typical: 90, max: 120 },
      blood_units: 1,
      priority: 'DELAYED'
    },
    soft_tissue: {
      name: 'Soft Tissue Repair/Debridement',
      duration_minutes: { min: 45, typical: 60, max: 120 },
      blood_units: 1,
      priority: 'DELAYED'
    },
    craniotomy_definitive: {
      name: 'Definitive Craniotomy',
      duration_minutes: { min: 180, typical: 240, max: 360 },
      blood_units: 4,
      priority: 'URGENT',
      specialty_required: 'NEUROSURGERY'
    },
    burns_excision: {
      name: 'Burn Excision and Grafting',
      duration_minutes: { min: 120, typical: 180, max: 300 },
      blood_units: 8,
      priority: 'DELAYED',
      specialty_required: 'BURNS'
    },
    abdominal_closure: {
      name: 'Delayed Abdominal Closure',
      duration_minutes: { min: 60, typical: 90, max: 150 },
      blood_units: 2,
      priority: 'SCHEDULED',
      dcs_phase: 3
    }
  },
  
  MINOR: {
    debridement: {
      name: 'Wound Debridement (Minor)',
      duration_minutes: { min: 15, typical: 30, max: 45 },
      blood_units: 0,
      priority: 'MINIMAL'
    },
    closure: {
      name: 'Wound Closure',
      duration_minutes: { min: 15, typical: 30, max: 60 },
      blood_units: 0,
      priority: 'MINIMAL'
    },
    foreign_body: {
      name: 'Foreign Body Removal',
      duration_minutes: { min: 20, typical: 45, max: 90 },
      blood_units: 0,
      priority: 'DELAYED'
    },
    chest_tube: {
      name: 'Chest Tube Insertion',
      duration_minutes: { min: 10, typical: 15, max: 30 },
      blood_units: 0,
      priority: 'URGENT'
    },
    tracheostomy: {
      name: 'Tracheostomy',
      duration_minutes: { min: 20, typical: 30, max: 45 },
      blood_units: 0,
      priority: 'URGENT'
    }
  }
};

// ============================================================================
// SURGICAL FATIGUE MODEL
// ============================================================================

export const SURGICAL_FATIGUE_MODEL = {
  PERFORMANCE_CURVE: [
    { hours: 0, performance: 1.00, error_rate: 0.02, decision_quality: 1.00 },
    { hours: 4, performance: 0.98, error_rate: 0.02, decision_quality: 0.98 },
    { hours: 8, performance: 0.95, error_rate: 0.03, decision_quality: 0.95 },
    { hours: 12, performance: 0.88, error_rate: 0.05, decision_quality: 0.85 },
    { hours: 16, performance: 0.78, error_rate: 0.08, decision_quality: 0.75 },
    { hours: 20, performance: 0.65, error_rate: 0.12, decision_quality: 0.60 },
    { hours: 24, performance: 0.50, error_rate: 0.18, decision_quality: 0.45 }
  ],
  
  RECOVERY: {
    minimum_rest_hours: 8,
    optimal_rest_hours: 12,
    full_recovery_hours: 24,
    recovery_rate_per_hour: 0.08,
    
    sleep_debt_accumulation: true,
    caffeine_temporary_boost: 0.10,
    caffeine_duration_hours: 4
  },
  
  SHIFT_RECOMMENDATIONS: {
    normal_operations: {
      shift_length_hours: 8,
      shifts_per_day: 3,
      days_on_before_rest: 7,
      sustainable: true
    },
    surge_operations: {
      shift_length_hours: 12,
      shifts_per_day: 2,
      days_on_before_rest: 3,
      max_duration_days: 7,
      sustainable: false
    },
    crisis_operations: {
      shift_length_hours: 16,
      shifts_per_day: 2,
      days_on_before_rest: 2,
      max_duration_days: 3,
      sustainable: false,
      notes: 'Accept increased error rate. Rotate teams.'
    }
  },
  
  FATIGUE_IMPACT: {
    mortality_increase_per_10_percent_fatigue: 0.02,
    complication_increase_per_10_percent_fatigue: 0.05,
    procedure_time_increase_per_10_percent_fatigue: 0.10,
    judgment_errors_increase: 0.03
  }
};

// ============================================================================
// MASS CASUALTY SURGICAL PROTOCOLS
// ============================================================================

export const MASCAL_SURGICAL_PROTOCOLS = {
  LEVELS: {
    LEVEL_1: {
      name: 'Minor MASCAL',
      casualties: { min: 1, max: 10 },
      or_utilization: 0.75,
      modifications: [
        'Prioritize T1 casualties',
        'Defer T3 procedures',
        'Standard DCS protocols'
      ]
    },
    LEVEL_2: {
      name: 'Moderate MASCAL',
      casualties: { min: 11, max: 25 },
      or_utilization: 1.0,
      modifications: [
        'All tables operational',
        'Call in off-duty staff',
        'DCS for all T1/T2',
        'Hold T3 for 24-48h'
      ]
    },
    LEVEL_3: {
      name: 'Major MASCAL',
      casualties: { min: 26, max: 50 },
      or_utilization: 1.25,
      modifications: [
        'Extended OR hours',
        'Minimal procedures only',
        'Increase T4 threshold',
        'Request augmentation'
      ]
    },
    LEVEL_4: {
      name: 'Overwhelming MASCAL',
      casualties: { min: 51, max: null },
      or_utilization: 1.50,
      modifications: [
        'Crisis standards of care',
        'Life-over-limb only',
        'Expectant category expanded',
        'Request evacuation halt'
      ],
      crisis_standards: true
    }
  },
  
  SURGICAL_PRIORITIES: {
    1: 'Airway obstruction',
    2: 'Massive hemorrhage (torso)',
    3: 'Tension pneumothorax',
    4: 'Massive hemorrhage (extremity)',
    5: 'Open fractures (vascular compromise)',
    6: 'Compartment syndrome',
    7: 'Closed fractures',
    8: 'Soft tissue wounds'
  },
  
  TIME_LIMITS: {
    airway: { max_minutes: 10 },
    hemorrhage_torso: { max_minutes: 30 },
    hemorrhage_extremity: { max_minutes: 60 },
    fasciotomy: { max_hours: 6 },
    open_fracture: { max_hours: 6 },
    debridement: { max_hours: 24 }
  },
  
  ABBREVIATED_PROCEDURES: {
    enabled_at: 'LEVEL_3',
    modifications: {
      skip_definitive_repair: true,
      pack_and_close: true,
      shunt_dont_repair: true,
      external_fix_only: true,
      no_primary_closure: true
    }
  }
};

// ============================================================================
// SURGICAL CAPACITY BY MTF TYPE
// ============================================================================

export const SURGICAL_CAPACITY_BY_MTF = {
  ROLE_1: {
    surgical_capability: false,
    surgical_teams: 0,
    operating_tables: 0,
    procedures_per_day: 0,
    notes: 'No surgical capability. Stabilization only.'
  },
  
  ROLE_2_BASIC: {
    surgical_capability: true,
    surgical_teams: 2,
    operating_tables: 2,
    procedures_per_day: 8,
    max_procedures_per_day: 12,
    capability: 'DAMAGE_CONTROL',
    specialties: ['General Surgery', 'Orthopedic'],
    hold_time_hours: 72,
    icu_beds: 0,
    notes: 'Damage control surgery. Must evacuate post-op.'
  },
  
  ROLE_2_ENHANCED: {
    surgical_capability: true,
    surgical_teams: 4,
    operating_tables: 4,
    procedures_per_day: 16,
    max_procedures_per_day: 24,
    capability: 'EXTENDED_DCS',
    specialties: ['General Surgery', 'Orthopedic', 'Vascular', 'Thoracic'],
    hold_time_hours: 72,
    icu_beds: 4,
    notes: 'Extended damage control. Some definitive procedures possible.'
  },
  
  ROLE_3: {
    surgical_capability: true,
    surgical_teams: 8,
    operating_tables: 8,
    procedures_per_day: 32,
    max_procedures_per_day: 48,
    capability: 'DEFINITIVE',
    specialties: [
      'General Surgery', 'Orthopedic', 'Vascular', 'Thoracic',
      'Neurosurgery', 'Maxillofacial', 'ENT', 'Ophthalmology'
    ],
    hold_time_hours: 168,
    icu_beds: 20,
    pacu_beds: 16,
    notes: 'Full surgical capability. Definitive care.'
  }
};

// ============================================================================
// SURGICAL DEMAND BY CASUALTY TYPE
// ============================================================================

export const SURGICAL_DEMAND = {
  BY_TRIAGE: {
    T1_IMMEDIATE: {
      surgery_probability: 0.95,
      procedures_per_patient: 2.5,
      dcs_required: 0.80,
      icu_required: 0.90,
      ventilator_required: 0.70,
      blood_units_average: 12,
      or_time_hours: 2.0
    },
    T2_DELAYED: {
      surgery_probability: 0.70,
      procedures_per_patient: 1.5,
      dcs_required: 0.20,
      icu_required: 0.30,
      ventilator_required: 0.15,
      blood_units_average: 4,
      or_time_hours: 1.5
    },
    T3_MINIMAL: {
      surgery_probability: 0.15,
      procedures_per_patient: 1.0,
      dcs_required: 0.0,
      icu_required: 0.0,
      ventilator_required: 0.0,
      blood_units_average: 0,
      or_time_hours: 1.0
    },
    T4_EXPECTANT: {
      surgery_probability: 0.10,
      procedures_per_patient: 1.0,
      dcs_required: 0.05,
      icu_required: 0.10,
      ventilator_required: 0.05,
      blood_units_average: 2,
      or_time_hours: 0.5
    }
  },
  
  BY_MECHANISM: {
    ARTILLERY: {
      surgery_probability: 0.85,
      procedures_per_patient: 2.2,
      dcs_required: 0.65,
      specialty_needs: {
        general: 0.60,
        orthopedic: 0.70,
        vascular: 0.25,
        thoracic: 0.15
      }
    },
    DRONE: {
      surgery_probability: 0.80,
      procedures_per_patient: 2.0,
      dcs_required: 0.55,
      specialty_needs: {
        general: 0.30,
        orthopedic: 0.40,
        maxillofacial: 0.45,
        neurosurgery: 0.28,
        ophthalmology: 0.18
      }
    },
    MINE_IED: {
      surgery_probability: 0.95,
      procedures_per_patient: 3.0,
      dcs_required: 0.85,
      specialty_needs: {
        general: 0.40,
        orthopedic: 0.80,
        vascular: 0.45,
        urology: 0.20
      }
    },
    SMALL_ARMS: {
      surgery_probability: 0.75,
      procedures_per_patient: 1.5,
      dcs_required: 0.40,
      specialty_needs: {
        general: 0.45,
        orthopedic: 0.50,
        vascular: 0.35
      }
    },
    VEHICLE_FIRE: {
      surgery_probability: 0.90,
      procedures_per_patient: 4.0,
      dcs_required: 0.70,
      specialty_needs: {
        general: 0.40,
        burns: 0.80,
        orthopedic: 0.30
      }
    }
  }
};

// ============================================================================
// SURGERY REQUIREMENTS BY TRIAGE (Calculator Integration)
// ============================================================================

export const SURGERY_REQUIREMENTS_BY_TRIAGE = {
  T1_IMMEDIATE: {
    surgery_required_percent: 0.95,
    average_duration_hours: 2.0,
    icu_required_percent: 0.90,
    ventilator_required_percent: 0.70,
    notes: 'Damage control surgery, massive transfusion typical.'
  },
  T2_DELAYED: {
    surgery_required_percent: 0.70,
    average_duration_hours: 1.5,
    icu_required_percent: 0.30,
    ventilator_required_percent: 0.15,
    notes: 'Can tolerate 4-6 hour delay.'
  },
  T3_MINIMAL: {
    surgery_required_percent: 0.15,
    average_duration_hours: 1.0,
    icu_required_percent: 0.0,
    ventilator_required_percent: 0.0,
    notes: 'Minor procedures, outpatient.'
  },
  T4_EXPECTANT: {
    surgery_required_percent: 0.10,
    average_duration_hours: 0.5,
    icu_required_percent: 0.10,
    ventilator_required_percent: 0.05,
    notes: 'Comfort care primary.'
  }
};

// ============================================================================
// SURGERY_RATE_BY_TRIAGE
// EXT-04: OR time allocation weight per triage category for surgical scheduling.
//
// DISTINCTION from SURGERY_REQUIREMENTS_BY_TRIAGE.surgery_required_percent:
//   surgery_required_percent = proportion of patients in category needing surgery
//   SURGERY_RATE_BY_TRIAGE   = relative OR slot weight for throughput calculations
//
// T1=1.0 → full OR slot (complex DCS, multiple teams, extended time)
// T2=0.5 → half OR slot (shorter procedure, single team, can share OR time)
// T3=0.25 → quarter OR slot (minor procedure, rapid turnover)
// T4=0.0 → no OR allocation (comfort care, no surgical intervention)
//
// Usage in OR scheduling:
//   OR_demand = Σ (casualties[T] × SURGERY_REQUIREMENTS_BY_TRIAGE[T].surgery_required_percent
//                               × SURGERY_RATE_BY_TRIAGE[T])
//   Divide by SURGICAL_CAPACITY_BY_MTF[role].procedures_per_day to get utilization.
//
// Consumed by:
//   timeToSurgeryModule.js — OR queue scheduling and wait time estimates
//   supplyIntelligence.js  — surgical supply consumption weighting
//
// References: NATO AMedP-1.7 Medical Planning Factors;
//   US Army ATP 4-02.10 Theater Hospitalization; DCS time studies
// ============================================================================

export const SURGERY_RATE_BY_TRIAGE = {
  T1_IMMEDIATE: 1.00,  // Full OR slot — DCS Phase 1, hemorrhage control, complex
  T2_DELAYED:   0.50,  // Half OR slot — can tolerate delay, shorter procedure
  T3_MINIMAL:   0.25,  // Quarter OR slot — minor procedure, rapid turnover
  T4_EXPECTANT: 0.00   // No OR allocation — comfort care, surgical resources preserved
};

// ============================================================================
// UKRAINE 2022-2024 LESSONS
// ============================================================================

export const UKRAINE_SURGICAL_LESSONS = {
  summary: 'Largest conventional war surgical experience since WWII',
  
  key_findings: {
    dcs_essential: 'DCS approach saved lives, 65% of surgical cases used DCS',
    specialty_shortages: 'Critical shortage of neurosurgeons and vascular surgeons forward',
    drone_injuries: 'Head/neck injuries from drones required maxillofacial capability',
    amputation_rate: '12% amputation rate, higher than expected',
    blood_products: 'Whole blood superior, FFP logistics challenging',
    fatigue: 'Surgeon fatigue major factor after 72h continuous operations'
  },
  
  specialty_demand: {
    orthopedic: 'Highest volume - 45% of surgical cases',
    general: 'Most DCS - 60% of DCS cases',
    vascular: 'High demand - 15% of cases, often limb-saving',
    neurosurgery: 'Shortage - 12% of cases, limited availability',
    maxillofacial: 'Increased - 15% of cases due to drones',
    burns: 'Vehicle fires - 8% of cases'
  },
  
  recommendations: [
    'Push vascular capability forward (Role 2)',
    'Train general surgeons in basic neuro',
    'Pre-position maxillofacial at Role 2E',
    'Plan for 12% amputation rate',
    'Rotate surgical teams every 72h max',
    'Stock FDP for forward areas'
  ]
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate surgical capacity for given configuration
 */
export const calculateSurgicalCapacity = (config) => {
  const {
    surgical_teams = 2,
    operating_tables = 2,
    hours_per_day = 16,
    avg_procedure_minutes = 90
  } = config;
  
  const procedures_per_table_per_hour = 60 / avg_procedure_minutes;
  const effective_tables = Math.min(surgical_teams, operating_tables);
  const procedures_per_day = Math.floor(effective_tables * procedures_per_table_per_hour * hours_per_day);
  
  return {
    surgical_teams,
    operating_tables,
    effective_tables,
    procedures_per_day,
    limiting_factor: surgical_teams < operating_tables ? 'TEAMS' : 'TABLES'
  };
};

/**
 * Calculate expected surgical demand
 */
export const calculateSurgicalDemand = (casualties) => {
  let total_procedures = 0;
  let dcs_procedures = 0;
  let icu_required = 0;
  let blood_units = 0;
  let or_hours = 0;
  
  const demand = SURGICAL_DEMAND.BY_TRIAGE;
  
  if (casualties.t1) {
    const d = demand.T1_IMMEDIATE;
    total_procedures += casualties.t1 * d.surgery_probability * d.procedures_per_patient;
    dcs_procedures += casualties.t1 * d.surgery_probability * d.dcs_required;
    icu_required += casualties.t1 * d.icu_required;
    blood_units += casualties.t1 * d.blood_units_average;
    or_hours += casualties.t1 * d.surgery_probability * d.or_time_hours;
  }
  
  if (casualties.t2) {
    const d = demand.T2_DELAYED;
    total_procedures += casualties.t2 * d.surgery_probability * d.procedures_per_patient;
    dcs_procedures += casualties.t2 * d.surgery_probability * d.dcs_required;
    icu_required += casualties.t2 * d.icu_required;
    blood_units += casualties.t2 * d.blood_units_average;
    or_hours += casualties.t2 * d.surgery_probability * d.or_time_hours;
  }
  
  if (casualties.t3) {
    const d = demand.T3_MINIMAL;
    total_procedures += casualties.t3 * d.surgery_probability * d.procedures_per_patient;
    or_hours += casualties.t3 * d.surgery_probability * d.or_time_hours;
  }
  
  return {
    total_procedures: Math.ceil(total_procedures),
    dcs_procedures: Math.ceil(dcs_procedures),
    icu_beds_needed: Math.ceil(icu_required),
    blood_units_needed: Math.ceil(blood_units),
    or_hours_needed: Math.round(or_hours * 10) / 10
  };
};

/**
 * Calculate surgical hours required
 */
export const calculateSurgicalHoursRequired = (casualties) => {
  const req = SURGERY_REQUIREMENTS_BY_TRIAGE;
  
  const immediate = (casualties.t1 || 0) * req.T1_IMMEDIATE.surgery_required_percent * req.T1_IMMEDIATE.average_duration_hours;
  const urgent = (casualties.t2 || 0) * req.T2_DELAYED.surgery_required_percent * req.T2_DELAYED.average_duration_hours;
  const delayed = (casualties.t3 || 0) * req.T3_MINIMAL.surgery_required_percent * req.T3_MINIMAL.average_duration_hours;
  const expectant = (casualties.t4 || 0) * req.T4_EXPECTANT.surgery_required_percent * req.T4_EXPECTANT.average_duration_hours;
  
  return {
    immediate_hours: Math.round(immediate * 10) / 10,
    urgent_hours: Math.round(urgent * 10) / 10,
    delayed_hours: Math.round(delayed * 10) / 10,
    expectant_hours: Math.round(expectant * 10) / 10,
    total_hours: Math.round((immediate + urgent + delayed + expectant) * 10) / 10
  };
};

/**
 * Get surgeon performance based on hours worked
 */
export const getSurgeonPerformance = (hoursWorked) => {
  const curve = SURGICAL_FATIGUE_MODEL.PERFORMANCE_CURVE;
  
  for (let i = 0; i < curve.length - 1; i++) {
    if (hoursWorked >= curve[i].hours && hoursWorked < curve[i + 1].hours) {
      const ratio = (hoursWorked - curve[i].hours) / (curve[i + 1].hours - curve[i].hours);
      return {
        hours_worked: hoursWorked,
        performance: curve[i].performance - (curve[i].performance - curve[i + 1].performance) * ratio,
        error_rate: curve[i].error_rate + (curve[i + 1].error_rate - curve[i].error_rate) * ratio,
        decision_quality: curve[i].decision_quality - (curve[i].decision_quality - curve[i + 1].decision_quality) * ratio
      };
    }
  }
  
  return {
    hours_worked: hoursWorked,
    performance: 0.50,
    error_rate: 0.20,
    decision_quality: 0.40
  };
};

/**
 * Get surgery requirement for triage category
 */
export const getSurgeryRequirement = (triageCategory) => {
  return SURGERY_REQUIREMENTS_BY_TRIAGE[triageCategory] || SURGERY_REQUIREMENTS_BY_TRIAGE.T2_DELAYED;
};

/**
 * Get procedure duration
 */
export const getProcedureDuration = (category, procedure) => {
  const cat = PROCEDURE_DURATIONS[category];
  if (!cat) return null;
  return cat[procedure] || null;
};

/**
 * Get MTF surgical capacity
 */
export const getMTFSurgicalCapacity = (mtfType) => {
  return SURGICAL_CAPACITY_BY_MTF[mtfType] || SURGICAL_CAPACITY_BY_MTF.ROLE_2_BASIC;
};

/**
 * Get specialty team data
 */
export const getSpecialtyTeam = (specialty) => {
  return SPECIALTY_SURGICAL_TEAMS[specialty] || null;
};

/**
 * Calculate MASCAL level
 */
export const getMASCALLevel = (casualtyCount) => {
  const levels = MASCAL_SURGICAL_PROTOCOLS.LEVELS;
  
  if (casualtyCount <= levels.LEVEL_1.casualties.max) return { ...levels.LEVEL_1, level: 1 };
  if (casualtyCount <= levels.LEVEL_2.casualties.max) return { ...levels.LEVEL_2, level: 2 };
  if (casualtyCount <= levels.LEVEL_3.casualties.max) return { ...levels.LEVEL_3, level: 3 };
  return { ...levels.LEVEL_4, level: 4 };
};

/**
 * Calculate backlog clearance time
 */
export const calculateBacklogClearance = (backlog, capacityPerDay) => {
  if (capacityPerDay === 0) return { days: Infinity };
  return {
    backlog,
    capacity_per_day: capacityPerDay,
    clearance_days: Math.ceil(backlog / capacityPerDay),
    clearance_hours: Math.ceil((backlog / capacityPerDay) * 24)
  };
};

// ============================================================================
// EXPORTS
// ============================================================================

// Named default export (fixes lint warning)
const surgicalCapacityConstants = {
  // Core structures
  DCS_PHASES,
  SURGICAL_TEAM_COMPOSITION,
  SPECIALTY_SURGICAL_TEAMS,
  ANESTHESIA_CAPACITY,
  STERILIZATION_CAPACITY,
  PROCEDURE_DURATIONS,
  SURGICAL_FATIGUE_MODEL,
  MASCAL_SURGICAL_PROTOCOLS,
  SURGICAL_CAPACITY_BY_MTF,
  SURGICAL_DEMAND,
  SURGERY_REQUIREMENTS_BY_TRIAGE,
  SURGERY_RATE_BY_TRIAGE,
  UKRAINE_SURGICAL_LESSONS,
  
  // Helper functions
  calculateSurgicalCapacity,
  calculateSurgicalDemand,
  calculateSurgicalHoursRequired,
  getSurgeonPerformance,
  getSurgeryRequirement,
  getProcedureDuration,
  getMTFSurgicalCapacity,
  getSpecialtyTeam,
  getMASCALLevel,
  calculateBacklogClearance
};

export default surgicalCapacityConstants;
